import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { courses, categories } from '@/mocks/courses';

const courseDetailMap: Record<string, string> = {
  '1': '/courses/chatgpt-mastery',
  '2': '/courses/ml-foundations',
  '3': '/courses/ai-agents',
  '4': '/courses/ai-for-devs',
};

export default function CoursesPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredCourses =
    activeCategory === 'All'
      ? courses
      : courses.filter((c) => c.category === activeCategory);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
            Course Catalog
          </p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            Master AI — From Beginner to Expert
          </h1>
          <p className="text-base text-foreground-600 max-w-2xl mx-auto">
            Practical, project-based AI education from working experts at leading tech companies. Every course comes with lifetime access, certificates, and hands-on projects.
          </p>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {filteredCourses.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                <i className="ri-search-line text-2xl text-foreground-400 w-6 h-6 flex items-center justify-center" />
              </div>
              <p className="text-foreground-500 text-sm">No courses found in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex flex-col h-full rounded-xl border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors group"
                >
                  <div className="relative overflow-hidden">
                    <img
                      alt={course.title}
                      className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500"
                      src={course.image}
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${course.difficultyColor}`}>
                        {course.difficulty}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 font-medium whitespace-nowrap transition-colors bg-secondary-100 text-secondary-900 text-xs">
                        {course.category}
                      </span>
                    </div>

                    <h3 className="font-bold text-base leading-snug mb-2 group-hover:text-primary-600 transition-colors font-heading">
                      {course.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 mb-4">{course.description}</p>

                    <div className="flex items-center gap-4 text-xs text-foreground-500 mb-4 flex-wrap">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line w-3 h-3 flex items-center justify-center" />
                        {course.hours}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-book-open-line w-3 h-3 flex items-center justify-center" />
                        {course.lessons}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line w-3 h-3 flex items-center justify-center" />
                        {course.students}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-xs w-3 h-3 flex items-center justify-center ${
                            i < Math.floor(parseFloat(course.rating)) ? 'text-primary-400' : 'text-background-300'
                          }`}
                        />
                      ))}
                      <span className="text-xs text-foreground-500 ml-1">{course.rating}</span>
                    </div>

                    <div className="mt-auto flex items-center justify-between">
                      <span className="text-2xl font-extrabold text-foreground-950">{course.price}</span>
                      <Link
                        to={courseDetailMap[course.id]}
                        className="inline-flex shrink-0 items-center justify-center text-sm font-medium whitespace-nowrap transition-all bg-primary-500 text-background-50 hover:bg-primary-600 h-9 gap-1.5 rounded-md px-4"
                      >
                        View Details
                        <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA Banner */}
          <div className="mt-14 text-center bg-primary-500 rounded-lg p-8">
            <h3 className="text-xl font-heading font-bold text-background-50 mb-2">
              Not sure where to start?
            </h3>
            <p className="text-sm text-primary-100 mb-5 max-w-md mx-auto">
              Try our ChatGPT & Prompt Engineering course — beginner-friendly and the perfect entry point into AI.
            </p>
            <Link
              to="/courses/chatgpt-mastery"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-background-50 text-primary-600 hover:bg-background-100 h-10 px-5 text-sm"
            >
              Start with ChatGPT Mastery
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}